import React, { useState, useEffect } from 'react'
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'

const OfferStoryViewer = ({ stories = [], startIndex = 0, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(startIndex)

  useEffect(() => {
    setCurrentIndex(startIndex)
  }, [startIndex])

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose()
      } else if (e.key === 'ArrowLeft') {
        if (currentIndex > 0) {
          setCurrentIndex(currentIndex - 1)
        }
      } else if (e.key === 'ArrowRight') {
        if (currentIndex < stories.length - 1) {
          setCurrentIndex(currentIndex + 1)
        } else {
          onClose()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [currentIndex, stories.length, onClose])

  if (!stories || stories.length === 0) {
    return null
  }

  const currentStory = stories[currentIndex]
  const imageUrl = currentStory?.images && currentStory.images.length > 0 ? currentStory.images[0] : null
  const title = currentStory?.title || 'Untitled Offer'
  
  // Calculate time left (24 hours from createdAt)
  const getTimeLeft = () => {
    if (!currentStory?.createdAt) return null
    const createdAt = new Date(currentStory.createdAt)
    const expiresAt = new Date(createdAt.getTime() + 24 * 60 * 60 * 1000)
    const now = new Date()
    const diffMs = expiresAt - now
    
    if (diffMs <= 0) return null
    
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))
    
    if (diffHours > 0) {
      return `${diffHours}h ${diffMinutes}m left`
    }
    return `${diffMinutes}m left`
  }

  const timeLeft = getTimeLeft()

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1)
    }
  }

  const handleNext = () => {
    if (currentIndex < stories.length - 1) {
      setCurrentIndex(currentIndex + 1)
    } else {
      onClose()
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black flex items-center justify-center">
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
        aria-label="Close"
      >
        <XMarkIcon className="w-6 h-6" />
      </button>

      {/* Time Left Badge */}
      {timeLeft && (
        <div className="absolute top-4 left-4 z-10 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-sm text-white text-xs font-semibold">
          {timeLeft}
        </div>
      )}

      {/* Story Counter */}
      {stories.length > 1 && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-sm text-white text-xs font-semibold">
          {currentIndex + 1} / {stories.length}
        </div>
      )}

      {/* Main Image */}
      <div className="relative w-full h-full flex items-center justify-center">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={title}
            className="max-w-full max-h-full object-contain"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-cyan-900 to-blue-900">
            <svg className="w-24 h-24 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
            </svg>
          </div>
        )}
      </div>

      {/* Title Overlay */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/60 to-transparent p-6 pt-12">
        <h2 className="text-2xl font-bold text-white mb-2">{title}</h2>
        {currentStory?.centerId?.organization && (
          <p className="text-sm text-white/80">From {currentStory.centerId.organization}</p>
        )}
      </div>

      {/* Navigation Buttons */}
      {currentIndex > 0 && (
        <button
          onClick={handlePrevious}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 p-3 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
          aria-label="Previous story"
        >
          <ChevronLeftIcon className="w-6 h-6" />
        </button>
      )}

      {currentIndex < stories.length - 1 && (
        <button
          onClick={handleNext}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 p-3 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
          aria-label="Next story"
        >
          <ChevronRightIcon className="w-6 h-6" />
        </button>
      )}

      {/* Progress Indicator */}
      {stories.length > 1 && (
        <div className="absolute top-0 left-0 right-0 h-1 flex gap-1 px-4 pt-4">
          {stories.map((_, index) => (
            <div
              key={index}
              className={`flex-1 h-full rounded-full transition-all ${
                index === currentIndex ? 'bg-white' : 'bg-white/30'
              }`}
            />
          ))}
        </div>
      )}

      {/* Tap Areas for Navigation (Mobile) */}
      <div className="absolute inset-0 flex">
        <div
          className="flex-1 cursor-pointer"
          onClick={handlePrevious}
          aria-hidden="true"
        />
        <div
          className="flex-1 cursor-pointer"
          onClick={handleNext}
          aria-hidden="true"
        />
      </div>
    </div>
  )
}

export default OfferStoryViewer
